/**
 * Import wizard step 3 — attach import to an existing pipeline or define a new one (schedule + template).
 *
 * Where: `ImportLeadsModal` before rules/templates step.
 * Uses: `Pipeline` objects from parent; MUI form controls.
 * Behavior: toggles create-new vs select-existing; captures active days/hours and template uuid.
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
} from '@mui/material';
import { Pipeline } from '@/api/pipeline';

interface Step3PipelineSelectionProps {
  createNew: boolean;
  pipelineUuid: string | null;
  pipelineName: string;
  activeDays: string[];
  activeHoursFrom: number | null;
  activeHoursTo: number | null;
  templateUuid: string | null;
  pipelines: Pipeline[];
  templates: Pipeline[];
  onCreateNewChange: (createNew: boolean) => void;
  onPipelineUuidChange: (uuid: string) => void;
  onPipelineNameChange: (name: string) => void;
  onActiveDaysChange: (days: string[]) => void;
  onActiveHoursFromChange: (hour: number | null) => void;
  onActiveHoursToChange: (hour: number | null) => void;
  onTemplateUuidChange: (uuid: string | null) => void;
}

const ACTIVE_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday', 'Weekdays', 'Weekends'];

export const Step3PipelineSelection: React.FC<Step3PipelineSelectionProps> = ({
  createNew,
  pipelineUuid,
  pipelineName,
  activeDays,
  activeHoursFrom,
  activeHoursTo,
  templateUuid,
  pipelines,
  templates,
  onCreateNewChange,
  onPipelineUuidChange,
  onPipelineNameChange,
  onActiveDaysChange,
  onActiveHoursFromChange,
  onActiveHoursToChange,
  onTemplateUuidChange,
}) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={createNew}
            onChange={(e) => onCreateNewChange(e.target.checked)}
          />
        }
        label="Create new pipeline"
      />
      
      {!createNew ? (
        <FormControl fullWidth>
          <InputLabel>Select Pipeline</InputLabel>
          <Select
            value={pipelineUuid || ''}
            onChange={(e) => onPipelineUuidChange(e.target.value)}
            label="Select Pipeline"
          >
            {pipelines.map((pipeline) => (
              <MenuItem key={pipeline.key} value={pipeline.key}>
                {pipeline.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <>
          <TextField
            label="Pipeline Name"
            fullWidth
            value={pipelineName}
            onChange={(e) => onPipelineNameChange(e.target.value)}
            required
          />
          <FormControl fullWidth required>
            <InputLabel>Template Pipeline *</InputLabel>
            <Select
              value={templateUuid || ''}
              onChange={(e) => onTemplateUuidChange(e.target.value || null)}
              label="Template Pipeline *"
              required
              error={!templateUuid}
            >
              {templates.map((template) => (
                <MenuItem key={template.key} value={template.key}>
                  {template.name}
                </MenuItem>
              ))}
            </Select>
            {!templateUuid && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                Template pipeline is required
              </Typography>
            )}
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Active Days</InputLabel>
            <Select
              multiple
              value={activeDays}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                onActiveDaysChange(value);
              }}
              label="Active Days"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
            >
              {ACTIVE_DAYS.map((day) => (
                <MenuItem key={day} value={day}>
                  <Checkbox checked={activeDays.indexOf(day) > -1} />
                  <Typography>{day}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Active Hours From *</InputLabel>
              <Select
                value={activeHoursFrom !== null ? String(activeHoursFrom) : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  onActiveHoursFromChange(value === '' ? null : Number(value));
                }}
                label="Active Hours From *"
                required
                error={activeHoursFrom === null}
              >
                {Array.from({ length: 25 }, (_, i) => (
                  <MenuItem key={i} value={String(i)}>
                    {i}:00
                  </MenuItem>
                ))}
              </Select>
              {activeHoursFrom === null && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  Active hours from is required
                </Typography>
              )}
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Active Hours To *</InputLabel>
              <Select
                value={activeHoursTo !== null ? String(activeHoursTo) : ''}
                onChange={(e) => {
                  const value = e.target.value;
                  onActiveHoursToChange(value === '' ? null : Number(value));
                }}
                label="Active Hours To *"
                required
                error={activeHoursTo === null}
              >
                {Array.from({ length: 25 }, (_, i) => (
                  <MenuItem key={i} value={String(i)}>
                    {i}:00
                  </MenuItem>
                ))}
              </Select>
              {activeHoursTo === null && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  Active hours to is required
                </Typography>
              )}
            </FormControl>
          </Box>
        </>
      )}
    </Box>
  );
};

